import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Chip,
  Box,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import { AdviseEngagement, AdviseCustomer, AdviseSupplier } from '../../../types/advise';
import { getAllProductTypes } from '../../../constants/advise/productTypes';

interface EngagementBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (engagement: Partial<AdviseEngagement>) => Promise<AdviseEngagement | null>;
  engagement: AdviseEngagement;
  customers: AdviseCustomer[];
  suppliers: AdviseSupplier[];
}

interface FormData {
  engagement_name: string;
  customer_id: string;
  product_type: string;
  selected_suppliers: string[];
}

const STEPS = ['Basic Info', 'Supplier Selection'];

export const EngagementBuilderDialog = ({
  open,
  onClose,
  onSave,
  engagement,
  customers,
  suppliers
}: EngagementBuilderDialogProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<FormData>({
    engagement_name: '',
    customer_id: '',
    product_type: '',
    selected_suppliers: []
  });

  useEffect(() => {
    if (open) {
      setForm({
        engagement_name: engagement.engagement_name || '',
        customer_id: engagement.customer_id || '',
        product_type: engagement.product_type || '',
        selected_suppliers: engagement.selected_suppliers || []
      });
      setActiveStep(0);
    }
  }, [open, engagement]);

  const productTypes = useMemo(() => getAllProductTypes(), []);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === form.customer_id),
    [customers, form.customer_id]
  );

  const handleChange = (field: keyof FormData, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSupplierToggle = (supplierId: string) => {
    setForm((prev) => {
      const current = prev.selected_suppliers;
      const updated = current.includes(supplierId)
        ? current.filter((id) => id !== supplierId)
        : [...current, supplierId];
      return { ...prev, selected_suppliers: updated };
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    const payload: Partial<AdviseEngagement> = {
      engagement_name: form.engagement_name,
      customer_id: form.customer_id,
      product_type: form.product_type,
      selected_suppliers: form.selected_suppliers
    };

    try {
      const result = await onSave(payload);
      if (result) onClose();
    } finally {
      setSaving(false);
    }
  };

  const canAdvance = () => {
    switch (activeStep) {
      case 0:
        return form.engagement_name.trim() !== '' && form.customer_id !== '' && form.product_type !== '';
      case 1:
        return form.selected_suppliers.length > 0;
      default:
        return false;
    }
  };

  // ---- Step Renderers ----

  const renderBasicInfo = () => (
    <Box className="flex flex-col gap-4">
      <TextField
        label="Engagement Name"
        value={form.engagement_name}
        onChange={(e) => handleChange('engagement_name', e.target.value)}
        fullWidth
        required
      />

      <FormControl
        fullWidth
        required>
        <InputLabel>Product Type</InputLabel>
        <Select
          value={form.product_type}
          label="Product Type"
          onChange={(e) => handleChange('product_type', e.target.value)}>
          {productTypes.map((pt) => (
            <MenuItem
              key={pt.id}
              value={pt.id}>
              {pt.name} — {pt.description}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label="Customer"
        value={selectedCustomer?.company_name || ''}
        disabled
      />

      {selectedCustomer && (
        <Card variant="outlined">
          <CardContent>
            <Typography
              variant="subtitle2"
              gutterBottom>
              Customer Profile
            </Typography>
            <Box className="flex flex-wrap gap-2">
              {selectedCustomer.industry && (
                <Chip
                  label={selectedCustomer.industry}
                  size="small"
                />
              )}
              {selectedCustomer.company_size && (
                <Chip
                  label={`${selectedCustomer.company_size} employees`}
                  size="small"
                />
              )}
              {selectedCustomer.budget_range && (
                <Chip
                  label={selectedCustomer.budget_range}
                  size="small"
                />
              )}
              {selectedCustomer.num_agents > 0 && (
                <Chip
                  label={`${selectedCustomer.num_agents} agents`}
                  size="small"
                />
              )}
              {selectedCustomer.timeline && (
                <Chip
                  label={selectedCustomer.timeline}
                  size="small"
                />
              )}
            </Box>
            {selectedCustomer.priority_requirements?.length > 0 && (
              <Box className="mt-2">
                <Typography
                  variant="caption"
                  color="textSecondary">
                  Priority Requirements
                </Typography>
                <Box className="flex flex-wrap gap-1 mt-1">
                  {selectedCustomer.priority_requirements.map((req) => (
                    <Chip
                      key={req}
                      label={req}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );

  const renderSupplierSelection = () => (
    <Box className="flex flex-col gap-2">
      <Typography
        variant="body2"
        color="textSecondary"
        className="mb-2">
        Select suppliers to include in this engagement.
      </Typography>
      {suppliers.length === 0 ? (
        <Alert severity="info">No suppliers available. Add suppliers first from the Suppliers page.</Alert>
      ) : (
        <Box className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {suppliers.map((supplier) => (
            <Card
              key={supplier.id}
              variant="outlined"
              className={`cursor-pointer transition-colors ${
                form.selected_suppliers.includes(supplier.id) ? 'border-blue-500 bg-blue-50' : ''
              }`}
              onClick={() => handleSupplierToggle(supplier.id)}>
              <CardContent className="flex items-start gap-3 p-3">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.selected_suppliers.includes(supplier.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleSupplierToggle(supplier.id)}
                    />
                  }
                  label=""
                  className="m-0"
                />
                <Box className="flex-1 min-w-0">
                  <Typography variant="subtitle2">{supplier.supplier_name}</Typography>
                  {supplier.headquarters && (
                    <Typography
                      variant="caption"
                      color="textSecondary">
                      {supplier.headquarters}
                    </Typography>
                  )}
                  <Box className="flex flex-wrap gap-1 mt-1">
                    {supplier.pricing_model && (
                      <Chip
                        label={supplier.pricing_model}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {supplier.strengths?.slice(0, 2).map((s) => (
                      <Chip
                        key={s}
                        label={s}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );

  const stepContent = [renderBasicInfo, renderSupplierSelection];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '70vh', maxHeight: '90vh' } }}>
      <DialogTitle>Edit Engagement</DialogTitle>
      <DialogContent dividers>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          className="mb-6">
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {stepContent[activeStep]()}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={saving}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button
            onClick={() => setActiveStep((s) => s - 1)}
            disabled={saving}>
            Back
          </Button>
        )}
        {activeStep < STEPS.length - 1 ? (
          <Button
            variant="contained"
            onClick={() => setActiveStep((s) => s + 1)}
            disabled={!canAdvance()}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canAdvance() || saving}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}>
            Save Changes
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
